import { useCallback } from "react";
import { useSWRConfig } from "swr";
import { auth } from "../../configs/FirebaseConfig";
import { FIRESTORE_HISTORY_SUBCOLLECTION, FIRESTORE_ORDERS_TABLE } from "../../constants/FirestoreConstant";
import { SHORTAGE_OUTCOMES } from "../../functions/shortages";
import { resolveFirestore } from "./shopFirestore";
import { hasHvoSession } from "./useProductFields";

/**
 * Recording what was agreed with a customer whose order left the freezer short (functions/
 * shortages): `picking.shortage = { outcome, note, at, by }` on the order in its own shop, with a
 * history entry. Only that one dot path: the projection of the scanners' events writes its own
 * `picking.*` paths and never this one, so neither can overwrite the other. The rules let the CMS
 * write `picking` also on a released pick day.
 */
export const useShortageHandling = () => {
  const { mutate } = useSWRConfig();

  /**
   * @param {Object} order - with `id` and `shopSource`
   * @param {{outcome: string|null, note?: string}} handling - `outcome: null` puts it back on "to call"
   */
  const saveHandling = useCallback(
    async (order, { outcome, note } = {}) => {
      if (outcome !== null && !SHORTAGE_OUTCOMES.includes(outcome)) throw new Error("shortages/unknown-outcome");
      if (order.shopSource === "HVO" && !hasHvoSession()) throw new Error("shortages/no-hvo-session");
      const firestore = resolveFirestore(order.shopSource);
      const reference = firestore.collection(FIRESTORE_ORDERS_TABLE).doc(order.id);
      const now = Date.now();
      const by = { uid: (auth.currentUser && auth.currentUser.uid) || null, email: (auth.currentUser && auth.currentUser.email) || null };
      const handling = outcome ? { outcome, note: (note || "").trim() || null, at: now, by } : null;

      const batch = firestore.batch();
      batch.update(reference, { "picking.shortage": handling });
      batch.set(reference.collection(FIRESTORE_HISTORY_SUBCOLLECTION).doc(), {
        timestamp: now,
        adminEmail: by.email,
        adminUid: by.uid,
        action: "shortage_handled",
        details: { outcome: outcome || null, note: handling ? handling.note : null, waveId: (order.picking && order.picking.waveId) || null },
      });
      await batch.commit();
      await mutate((key) => Array.isArray(key) && (key[0] === "pickDayOrders" || key[0] === "orders"));
      return handling;
    },
    [mutate],
  );

  return { saveHandling };
};
